import { apiClient } from '@app/lib'
import { useSugarContext } from '@app/providers'
import { type SugarProviderPhotoList } from '@app/types'
import { useQuery } from '@tanstack/react-query'
import ms from 'ms'
import { useSession } from 'next-auth/react'

// Type guard distinguishing the success body (PhotoList) from the retriable
// failure body (StandardResponse with success: false). Both arrive as HTTP 200.
const isPhotoList = (body: unknown): body is SugarProviderPhotoList =>
  !!body && typeof body === 'object' && 'photos' in body && 'limits' in body

export const useProviderPhotos = () => {
  const session = useSession()
  const { siteId } = useSugarContext()

  return useQuery({
    enabled: session.status === 'authenticated' && Boolean(siteId),
    queryKey: ['provider-photos', { siteId }],
    queryFn: async ({ signal }) => {
      const { data } = await apiClient.get<unknown>('/api/provider-profile/photos', {
        params: { siteId },
        signal
      })

      if (!isPhotoList(data)) {
        // Retriable failure path — let React Query surface it as a query error.
        throw new Error('Failed to load photos. Please try again.')
      }

      return data
    },
    staleTime: ms('5m')
  })
}
